import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { OfflineProvider } from '@/contexts/OfflineContext';
import { Toaster } from '@/components/ui/sonner';
import GamePage from '@/pages/GamePage';
import AuthCallback from '@/pages/AuthCallback';
import Layout from '@/components/Layout';
import DashboardPage from '@/pages/DashboardPage';
import SitesPage from '@/pages/SitesPage';
import EntriesPage from '@/pages/EntriesPage';
import LedgerPage from '@/pages/LedgerPage';
import CombinedLedgerPage from '@/pages/CombinedLedgerPage';
import EntitiesPage from '@/pages/EntitiesPage';
import SubCategoriesPage from '@/pages/SubCategoriesPage';
import PartnersPage from '@/pages/PartnersPage';
import CashBankPage from '@/pages/CashBankPage';
import AuditLogPage from '@/pages/AuditLogPage';
import TransfersPage from '@/pages/TransfersPage';
import UsersPage from '@/pages/UsersPage';
import TrashPage from '@/pages/TrashPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8ef]">
        <div className="w-8 h-8 border-2 border-[#bbada0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicRoute><GamePage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><GamePage /></PublicRoute>} />
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="entries" element={<EntriesPage />} />
        <Route path="transfers" element={<TransfersPage />} />
        <Route path="ledger" element={<LedgerPage />} />
        <Route path="combined-ledger" element={<CombinedLedgerPage />} />
        <Route path="entities" element={<EntitiesPage />} />
        <Route path="sub-categories" element={<SubCategoriesPage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="cash-bank" element={<CashBankPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="trash" element={<TrashPage />} />
      </Route>
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/sites" element={<Navigate to="/app/sites" replace />} />
      <Route path="/entries" element={<Navigate to="/app/entries" replace />} />
      <Route path="/transfers" element={<Navigate to="/app/transfers" replace />} />
      <Route path="/ledger" element={<Navigate to="/app/ledger" replace />} />
      <Route path="/combined-ledger" element={<Navigate to="/app/combined-ledger" replace />} />
      <Route path="/entities" element={<Navigate to="/app/entities" replace />} />
      <Route path="/sub-categories" element={<Navigate to="/app/sub-categories" replace />} />
      <Route path="/partners" element={<Navigate to="/app/partners" replace />} />
      <Route path="/cash-bank" element={<Navigate to="/app/cash-bank" replace />} />
      <Route path="/audit-log" element={<Navigate to="/app/audit-log" replace />} />
      <Route path="/users" element={<Navigate to="/app/users" replace />} />
      <Route path="/trash" element={<Navigate to="/app/trash" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
            <AppRouter />
            <Toaster />
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
